import React, { useEffect, useRef, useState } from 'react';
import './Navbar.scss';
import Logo from '../../images/icons/logo.svg';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';
import userStore from '../../MobXStorage/UserStore';
import { observer } from 'mobx-react-lite';
import { useIsAuthorized, useIsAdmin, useIsMaster } from '../../hooks/UserHooks';
import UserIcon from '../../images/icons/user.svg';
import { trackPageView } from '../../analytics';

const Navbar: React.FC = observer(() => {
    const { t } = useTranslation();
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLUListElement | null>(null);
    const userButtonRef = useRef<HTMLDivElement | null>(null);

    const isAuthorized = useIsAuthorized();
    const isAdmin = useIsAdmin();
    const isMaster = useIsMaster();
    const licenseType = localStorage.getItem('licenseType'); // simple + always current on render

    const history = useHistory();
    const loc = useLocation();

    useEffect(() => {
        trackPageView(loc.pathname + loc.search);
    }, [loc.pathname, loc.search]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                userButtonRef.current &&
                !userButtonRef.current.contains(target)
            ) {
                setShowDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const logout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('licenseType');
        userStore.clearToken();
        userStore.roles = [];
        setShowDropdown(false);
        history.push('/login');
    };

    return (
        <nav className="navbar">
            <Link className="navbar-brand" to="/">
                <img src={Logo} alt="lifesightme - logo" />
            </Link>

            <div>
                <ul>
                    {isAuthorized && (
                        <li className="nav-item">
                            <Link className="body body-normal nav-link" to="/explanation">
                                {t('components.navbar.explanation')}
                            </Link>
                        </li>
                    )}

                    {isMaster && (
                        <li className="nav-item">
                            <Link className="body body-normal nav-link" to="/admin-settings">
                                {t('components.navbar.settings')}
                            </Link>
                        </li>
                    )}

                    {isAdmin && licenseType !== 'Demo' && licenseType !== 'Basic' && (
                        <li className="nav-item">
                            <Link className="body body-normal nav-link" to="/team">
                                {t('components.navbar.team')}
                            </Link>
                        </li>
                    )}

                    {licenseType != null && licenseType !== 'Student' && (
                        <li className="nav-item">
                            <Link className="body body-normal nav-link" to="/subscription">
                                {t('components.navbar.subscription')}
                            </Link>
                        </li>
                    )}
                </ul>

                <ul>
                    <li className="nav-item">
                        <LanguageSwitch />
                    </li>

                    {isAuthorized ? (
                        <li className="nav-item nav-user-wrapper" style={{ position: 'relative' }}>
                            <div
                                ref={userButtonRef}
                                className="body body-normal nav-link nav-user"
                                onClick={() => setShowDropdown((prev) => !prev)}
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'white' }}
                            >
                                {t('components.navbar.loggedIn')}
                                <img src={UserIcon} alt="User Icon" className="user-icon" />
                            </div>

                            {showDropdown && (
                                <ul
                                    ref={dropdownRef}
                                    className="dropdown-menu"
                                    style={{
                                        position: 'absolute',
                                        top: '100%',
                                        right: 0,
                                        background: 'white',
                                        listStyle: 'none',
                                        margin: 0,
                                        padding: '8px 0',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                        borderRadius: '4px',
                                        zIndex: 10,
                                        minWidth: '120px',
                                        fontSize: '14px',
                                    }}
                                >
                                    <li
                                        className="dropdown-item"
                                        style={{
                                            padding: '2px 16px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                        }}
                                        onClick={() => logout()}
                                    >
                                        {t('components.navbar.logout')}
                                    </li>
                                </ul>
                            )}
                        </li>
                    ) : (
                        <li className="nav-item">
                            <Link className="body body-normal nav-link nav-user" to="/login">
                                {t('pages.login-page.Login')}
                                <img src={UserIcon} alt="User Icon" className="user-icon" />
                            </Link>
                        </li>
                    )}
                </ul>
            </div>
        </nav>
    );
});

export default Navbar;
